// The top of the dashboard: who is reading, what day it is, and how far back
// the numbers below go.
//
// The greeting uses the workspace's name rather than the person's, and that is
// deliberate: an agency user signed into four workspaces needs to know which
// one they are looking at far more than they need to be told good morning.

import type { ReactNode } from "react";

/** The windows worth offering. Longer than 90 days is a chart of one pixel per
 *  day that nobody reads; the server clamps at 180 regardless. */
export const RANGES: Array<{ days: number; label: string }> = [
  { days: 7, label: "7 days" },
  { days: 30, label: "30 days" },
  { days: 90, label: "90 days" },
];

function greeting(now: Date): string {
  const h = now.getHours();
  if (h < 12) return "Good morning";
  if (h < 18) return "Good afternoon";
  return "Good evening";
}

export function DashboardHeader({
  workspaceName, days, onDays, busy,
}: {
  workspaceName: string | null;
  days: number;
  onDays: (days: number) => void;
  /** Re-reading with a new window — the range buttons stay usable, the numbers
   *  below go quiet. */
  busy: boolean;
}) {
  const now = new Date();
  const date: ReactNode = now.toLocaleDateString(undefined, {
    weekday: "long", day: "numeric", month: "long",
  });

  return (
    <header className="dash-head">
      <div>
        <h1>
          {greeting(now)}{workspaceName ? <>, {workspaceName}</> : null}
        </h1>
        <p className="muted">
          {date} — here is what this workspace has sent, and what is waiting to go.
        </p>
      </div>

      <div className="segmented" role="group" aria-label="Time range">
        {RANGES.map((r) => (
          <button key={r.days} type="button"
                  className={days === r.days ? "on" : ""}
                  aria-pressed={days === r.days}
                  aria-busy={busy && days === r.days}
                  onClick={() => onDays(r.days)}>
            {r.label}
          </button>
        ))}
      </div>
    </header>
  );
}
